import { v4 as uuidv4 } from 'uuid';
import { getArrayWithUniqueItems } from '@/helpers/utils';
import { ModelSerieAdmin as ModelSerie, ModelSeriesTVRow, ModelSeriesViewRow, Plant, PlantsViewRow, TypeVersion, TreeStatusSummary } from '@/types';

export const getPlantNamesByCodes = (plantsCode: string[], plants: Plant[]) => plantsCode
  .map((plantCode) => plants.find((plant) => plant.wkzNumber === plantCode)?.plantName || '').join(' | ');

export const mapChildrenModelSeriesRows = (ms: ModelSerie, plants: Plant[]): ModelSeriesTVRow[] => (
  ms.typeVersions.map((tv: TypeVersion) => ({
    idGenerated: uuidv4(),
    modelSeriesRaw: ms.modelSeries,
    modelSeries: '',
    typeVersion: tv.code.toUpperCase(),
    type: ms.type || '',
    activeForEfa: tv.activeForEfa,
    dialog: tv.status,
    rawPlants: tv.plants,
    plants: getPlantNamesByCodes(tv.plants, plants),
    isChild: true,
  }))
);

export const mapChildrenPlantsRows = (plant: Plant, modelSeriesByPlant: ModelSerie[]) => {
  const mapedRows = modelSeriesByPlant.map((ms) => ms.typeVersions.map((tv: TypeVersion) => ({
    idGenerated: uuidv4(),
    modelSeriesRaw: ms.modelSeries,
    modelSeries: ms.modelSeries.toUpperCase(),
    typeVersion: tv.code.toUpperCase(),
    activeForEfa: tv.activeForEfa,
    dialog: tv.status,
    rawPlants: tv.plants,
    plant: plant.plantName,
    isChild: true,
  })));

  return mapedRows.flat();
};

export const mapModelSeriesTableRows = (modelSeries: ModelSerie[], plantsColumns: Plant[],
  lastAllModelSeries: ModelSeriesViewRow[]): ModelSeriesViewRow[] => {
  const mapedRows: ModelSeriesViewRow[] = [];

  modelSeries.forEach((ms: ModelSerie) => {
    const rawPlants = ms.typeVersions.reduce((acc: string[], curr: TypeVersion) => acc.concat(curr.plants), []);
    const modelSeriesRow = {
      // idGenerated like here for keep the row expanded when change efa checkbox
      idGenerated: lastAllModelSeries.find((msR) => msR.modelSeries === ms.modelSeries)?.idGenerated ?? uuidv4(),
      modelSeries: ms.modelSeries.toUpperCase(),
      typeVersion: ms.typeVersions.map((tv: TypeVersion) => tv.code.toUpperCase()).join(' | '),
      type: ms.type || '',
      activeForEfa: {
        total: ms.typeVersions.length,
        active: ms.typeVersions.reduce((acc: number, curr: TypeVersion) => (curr.activeForEfa ? acc + 1 : acc), 0),
      },
      dialog: {
        total: ms.typeVersions.length,
        active: ms.typeVersions.reduce((acc: number, curr: TypeVersion) => (curr.status ? acc + 1 : acc), 0),
      },
      plants: getPlantNamesByCodes(getArrayWithUniqueItems(rawPlants), plantsColumns),
      rawPlants,
      children: mapChildrenModelSeriesRows(ms, plantsColumns),
      isChild: false,
    };
    mapedRows.push(modelSeriesRow);
  });

  return mapedRows;
};

export const mapPlantsTableRows = (modelSeries: ModelSerie[], plantsColumns: Plant[],
  lastAllPlants: PlantsViewRow[]): PlantsViewRow[] => {
  const mapedRows: PlantsViewRow[] = [];

  plantsColumns.forEach((p: Plant) => {
    const modelSeriesByPlant = modelSeries.filter((ms) => ms.typeVersions.some((tv) => (tv.plants.includes(p.wkzNumber))))
      .map((ms) => ({ ...ms, typeVersions: ms.typeVersions.filter((tv) => tv.plants.includes(p.wkzNumber)) }));
    const msTVcount = modelSeriesByPlant.reduce((acc: number, curr: ModelSerie) => acc + curr.typeVersions.length, 0);
    const plantsRow = {
      // idGenerated like here for keep the row expanded when change efa checkbox
      idGenerated: lastAllPlants.find((pR) => pR.plant === p.plantName)?.idGenerated ?? uuidv4(),
      plant: p.plantName,
      wkzNumber: p.wkzNumber,
      modelSeries: [...new Set(modelSeriesByPlant.map((ms) => ms.modelSeries.toUpperCase()))].join(' | '),
      typeVersion: [...new Set(modelSeriesByPlant.map((ms) => ms.typeVersions.map((tv) => tv.code.toUpperCase())).flat())]
        .join(' | '),
      activeForEfa: {
        total: msTVcount,
        active: modelSeriesByPlant.reduce((acc: number, curr: ModelSerie) => (curr.typeVersions
          .reduce((accTV: number, currTV: TypeVersion) => (currTV.activeForEfa ? accTV + 1 : accTV), 0) + acc), 0),
      },
      dialog: {
        total: msTVcount,
        active: modelSeriesByPlant.reduce((acc: number, curr: ModelSerie) => (curr.typeVersions
          .reduce((accTV: number, currTV: TypeVersion) => (currTV.status ? accTV + 1 : accTV), 0) + acc), 0),
      },
      children: mapChildrenPlantsRows(p, modelSeriesByPlant),
      rawModelSeries: modelSeriesByPlant,
      isChild: false,
    };

    mapedRows.push(plantsRow);
  });

  return mapedRows;
};

export const generateModelSeriesState = (modelSeriesRaw: ModelSerie[], plantsActive: Plant[], lastAllModelSeries: any[] = []) => {
  const modelSeriesOrdered = modelSeriesRaw.sort((a, b) => a.modelSeries.localeCompare(b.modelSeries));
  const modelSeriesViewRow = mapModelSeriesTableRows(modelSeriesOrdered, plantsActive, lastAllModelSeries);
  const plantsViewRow = mapPlantsTableRows(modelSeriesOrdered, plantsActive, lastAllModelSeries);

  return {
    plantsActive,
    allModelSeriesRaw: modelSeriesOrdered,
    modelSeriesView: {
      allModelSeries: modelSeriesViewRow,
      dialogInactive: modelSeriesViewRow.filter((ms) => ms.dialog.total > ms.dialog.active),
      efaInactive: modelSeriesViewRow.filter((ms) => ms.activeForEfa.total > ms.activeForEfa.active),
      noPlantAssigment: modelSeriesViewRow.filter((ms) => !ms.rawPlants.length),
    },
    plantsView: {
      allModelSeries: plantsViewRow,
      dialogInactive: plantsViewRow.filter((ms) => ms.dialog.total > ms.dialog.active),
      efaInactive: plantsViewRow.filter((ms) => ms.activeForEfa.total > ms.activeForEfa.active),
      noPlantAssigment: plantsViewRow.filter((ms) => !ms.rawModelSeries.length),
    },
  };
};

export const getTreeStatusSummaryValues = (value: unknown) => {
  const parentValue = value as TreeStatusSummary;
  const active = parentValue?.active ?? 0;
  const total = parentValue?.total ?? 0;

  return { parentValue, active, total };
};
